import { useEffect } from 'react';
import { AdjustPopoverOverflow, TriggerType } from './popover';
import { Modifier } from '@popperjs/core';

// be aware!! equal to $popover-transition-time in popover.scss
export const POPOVER_TRANSITION_TIME = 120;
export const ARROW_OFFSET = 8;

export const setPopperModifiers = (
	adjustOverflow: AdjustPopoverOverflow,
	offset: number[],
	arrowElement: HTMLDivElement
) => {
	let _offset = [...offset];

	if (arrowElement) {
		_offset[1] = offset[1] + ARROW_OFFSET;
	}

	const modifier: Partial<Modifier<string, object>>[] = [
		{
			name: 'offset',
			options: {
				offset: _offset
			}
		},
		{
			name: 'arrow',
			options: {
				element: arrowElement
			}
		}
	];

	const shouldAdjust = adjustOverflow === AdjustPopoverOverflow.ADJUST;

	if (!shouldAdjust) {
		modifier.push(
			{
				name: 'flip',
				options: {
					fallbackPlacements: ['bottom-start']
				}
			},
			{
				name: 'preventOverflow',
				options: {
					mainAxis: false
				}
			}
		);
	}

	return modifier;
};

export const Events: { [key in TriggerType]: string[] } = {
	hover: ['mousemove'],
	click: ['mousedown', 'touchstart']
};

export const useOutsidePopper = (args: {
	popperRef: HTMLDivElement;
	triggerRef: HTMLElement;
	isRefVisible: boolean;
	hasPopOverMousedown: React.MutableRefObject<boolean>;
	callback: () => void;
	trigger: TriggerType[];
	shouldCloseOnOutsideClick?: (event: Event) => boolean;
}) => {
	const {
		popperRef,
		triggerRef,
		isRefVisible,
		hasPopOverMousedown,
		callback,
		trigger: actionType,
		shouldCloseOnOutsideClick
	} = args;

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;

		const handleEventOutside = event => {
			if (
				!popperRef?.contains(event.target) &&
				!triggerRef?.contains(event.target) &&
				isRefVisible &&
				!hasPopOverMousedown.current
			) {
				const shouldClose = shouldCloseOnOutsideClick ? shouldCloseOnOutsideClick(event) : true;

				if (shouldClose) {
					timeoutId = setTimeout(callback, POPOVER_TRANSITION_TIME);
					removeListeners(actionType, handleEventOutside);
				}
			}
		};

		if (isRefVisible) {
			setListeners(actionType, handleEventOutside);
		}
		return () => {
			removeListeners(actionType, handleEventOutside);
			clearTimeout(timeoutId);
		};
	}, [popperRef, isRefVisible]);
};

const setListeners = (trigger: TriggerType[], callback: (event: any) => void) => {
	trigger.forEach(action => {
		Events[action].forEach(eventName => {
			document.addEventListener(eventName, callback);
		});
	});
};

const removeListeners = (trigger: TriggerType[], callback: (event: any) => void) => {
	trigger.forEach(action => {
		Events[action].forEach(eventName => {
			document.removeEventListener(eventName, callback);
		});
	});
};
